import { Box } from '@mui/material';
import { GetMeResponseDTO } from '@src/services/brain/modules/v1/auth/dto/brain-auth.getMe.response.dto';
import { staggerChildrenSX } from '@src/styles/animations';
import {
  ProfileHeader,
  ProfileInfoCard,
  ProfileOrganizationsCard,
  ProfileSignatureCard,
  ProfileHeaderSkeleton,
  ProfileInfoCardSkeleton,
  ProfileOrganizationsCardSkeleton,
  ProfileSignatureCardSkeleton,
} from './components';
import { ProfileBearerCard } from './components/ProfileBearerCard';

export type TProfileView = {
  userData: GetMeResponseDTO & { organizations: { id: string; fantasyName: string }[] };
  signatureKey: string | null;
  isLoadingMe: boolean;
  isLoadingSignature: boolean;
  isRefreshingSignature: boolean;
  error: string | null;
  showSignature: boolean;
  organizationsDisplayLimit: number;
  onGetSignature: () => void;
  onRefreshSignature: () => void;
  onCopySignature: () => void;
  onToggleSignatureVisibility: () => void;
  onLoadMoreOrganizations: () => void;
  onCopyBearer: () => void;
};

export const ProfileView = ({
  userData,
  signatureKey,
  isLoadingMe,
  isLoadingSignature,
  isRefreshingSignature,
  showSignature,
  organizationsDisplayLimit,
  onGetSignature,
  onRefreshSignature,
  onCopySignature,
  onToggleSignatureVisibility,
  onLoadMoreOrganizations,
  onCopyBearer,
}: TProfileView) => {
  return (
    <Box
      sx={{
        boxSizing: 'border-box',
        height: { xs: 'auto', md: 'calc(100vh - 32px)' },
        overflowY: { md: 'auto' },
        display: 'flex',
        alignItems: { xs: 'flex-start', md: 'safe center' },
        justifyContent: 'center',
        paddingTop: { xs: 1, md: 0 },
        paddingBottom: { xs: 1, md: '30px' },
      }}
    >
      <Box
        sx={{
          maxWidth: 1040,
          width: '100%',
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(0, 1fr)' },
          gap: 2,
          ...staggerChildrenSX('*', 6, 45),
        }}
      >
        {isLoadingMe ? (
          <>
            <ProfileHeaderSkeleton />
            <ProfileInfoCardSkeleton />
            <ProfileOrganizationsCardSkeleton />
            <ProfileSignatureCardSkeleton />
            <ProfileSignatureCardSkeleton />
          </>
        ) : (
          <>
            <ProfileHeader
              name={userData?.member.name || ''}
              email={userData?.member.email}
              isAdmin={userData?.isAdmin}
            />

            <ProfileInfoCard
              userId={userData?.id || ''}
              role={userData?.role}
              team={userData?.member?.team}
              squads={userData?.member?.squads}
            />

            <ProfileOrganizationsCard
              organizations={userData?.organizations || []}
              organizationsDisplayLimit={organizationsDisplayLimit}
              onLoadMoreOrganizations={onLoadMoreOrganizations}
            />

            <ProfileSignatureCard
              signatureKey={signatureKey}
              isLoadingSignature={isLoadingSignature}
              isRefreshingSignature={isRefreshingSignature}
              showSignature={showSignature}
              onGetSignature={onGetSignature}
              onRefreshSignature={onRefreshSignature}
              onCopySignature={onCopySignature}
              onToggleSignatureVisibility={onToggleSignatureVisibility}
            />

            <ProfileBearerCard onCopyBearer={onCopyBearer} />
          </>
        )}
      </Box>
    </Box>
  );
};

export default ProfileView;
